// All cross-system TEST VALUES from plan §28 in one place (mirrors the intent of tuning.json).
// Balance passes should touch only this file.

export const TUNING = {
  tickRate: 20, // Hz (plan §7.2)
  dt: 1 / 20,

  // §28.1 match start
  startPower: 450,
  startEssence: 25,
  startWorkers: 0, // owner feedback: no free workers — train the first ones yourself
  workerCost: 60,
  workerTrainTime: 12,
  // per-zone worker cap is per-faction (workersPerZone) — see content/factions.ts
  commandCore: 18,
  commandPerSite: 5,
  commandAscension: { 2: 4, 3: 6, 4: 8 } as Record<number, number>,
  allocationDefaultPower: 90,

  // §28.2 economy
  carryCapacity: 10,
  gatherChannelSeconds: 2,
  repairHpPerSecond: 8,
  repairPowerPer4Hp: 1,
  repairMaxWorkers: 4,
  repairDiminish: [1, 0.75, 0.5, 0.25],
  constructionMaxWorkers: 4,
  constructionDiminish: [1, 0.75, 0.5, 0.25],

  // §28.3 sites & capture
  siteTricklePowerPerSec: 0.6, // TEST — owned sites pay a passive trickle (owner request; not in bible)
  siteTrickleEssencePerSec: 0.25, // TEST
  captureRadius: 10,
  defendRadius: 18,
  buildRadius: 22,
  majorStructuresPerSite: 3,
  coreTerritoryDeckBuildings: 4,
  // capture weight → rate lookup so 1/2/3 squads ≈ 18/12/8 s (§12.2)
  captureRateByWeight: [0, 100 / 18, 100 / 12, 100 / 8] as number[],
  captureDecayGraceSeconds: 3,
  captureDecayPerSecond: 25,
  recoveryPerSecond: 0.02, // fraction of max HP
  bossRecoveryPerSecond: 0.01,
  recoveryOutOfCombatSeconds: 5,

  // §28.4 core
  coreAssaultRepairWorkerCap: 2,
  coreAssaultWindowSeconds: 5,
  lastCourseThreshold: 0.3,
  lastCourseRepairResist: 0.2,
  murderSlotsDps: 12,
  murderSlotsRange: 1.5,

  // armor bands → physical reduction (A-33)
  armorReduction: {
    none: 0,
    light: 0.15,
    moderate: 0.3,
    heavy: 0.45,
    'very-heavy': 0.55,
  } as Record<string, number>,

  // construction / refunds (A-21)
  blueprintCancelRefund: 0.75,
  queueDestroyRefund: 0.5,

  // castle walls (owner request — see README Deviations)
  wallClaimCost: 150, // TEST — default; ring segments override per-ruin
  wallRebuildCooldownSeconds: 60, // TEST — lockout after a wall dies in combat
  wallConstructingDamageTakenMult: 2.0, // TEST — walls are fragile while raising
  wallGateTriggerDistance: 6, // TEST — friendly traffic opens a gate before reaching the doors
  wallGateClearanceDistance: 0.35, // TEST — body-edge safety; nearby idle guards do not hold it open
  wallGateOpenSeconds: 0.35, // TEST — fast enough that auto-gates do not interrupt an order
  wallGateCloseSeconds: 0.55, // TEST — closing remains legible and gives traffic time to clear
  wallGateHoldSeconds: 1.5, // TEST — prevents rapid open/close chatter between squads

  // shelter capacity is per-faction — see content/factions.ts
  shelterReleaseInterval: 0.3,

  // combat feel
  acquisitionExtraRange: 6,
  leashMult: 1.5,
  retargetIntervalSeconds: 0.25,
  focusBonus: 1.2,
  withdrawSpeedBonus: 1.1,
  combatPlantSeconds: 1.0, // TEST — fighters ignore crowd separation this long after combat
  meleeStrikePauseSeconds: 0.25, // TEST — melee attacker roots briefly on each swing (Battleforge stop-to-strike)
  // Attack wind-up. Damage used to land the instant the cooldown expired, with the melee root
  // applied *after* the swing — the same total frozen time, but reading as a stutter rather than
  // as a telegraphed blow. The root now happens during the wind-up instead, so the pause is the
  // anticipation before the hit. Fraction of the attack period, capped for slow weapons.
  windupFraction: 0.25, // TEST
  windupMaxSeconds: 0.3, // TEST
  attackChaseLeash: 35, // TEST — explicit attack orders give up pursuit beyond this from the order anchor (was infinite)
  chaseFutileSeconds: 2.5, // TEST — no landed hit on the current target this long → drop target stickiness, prefer nearest (SC2-style switch)

  // workers (health/speed live on the faction's worker unit def)
  workerFleeRadius: 8,
  workerFleeSeconds: 4,
  overCapacityRateMult: 0.5, // TEST — workers beyond a zone's cap gather at half rate (owner request)

  // deposits are per-map; danger factor for the solver
  solverDangerRadius: 15,

  // countdown before match
  countdownSeconds: 3,

  // upgrade research time (§16.5)
  upgradeResearchSeconds: 20,

  // boss
  bossSummonLocalSeconds: 3,

  // corpse economy substrate (Ashen; dormant otherwise)
  corpseMarkDuration: 25, // TEST
  // Both of these silently dropped triggers in large battles: a single engagement can kill more
  // than 16 squads in one tick, which starved allyDiedNearby triggers and resurrection rituals,
  // and 40 corpse marks is well under what an Ashen army generates in a real fight. // TEST
  corpseMarkCap: 96,
  recentDeathBuffer: 64,

  // squad mounting (§27)
  mountRange: 8,
  mountRangeBonus: 1.25,

  // over-time caps
  maxSquadsSoft: 60,
} as const;
